/**
 * Where a value came from.
 *
 * The AI variant is violet and carries a sparkle, matching every other AI
 * surface in the app; everything else is quiet gray. In a finance tool you must
 * always be able to tell at a glance whether you are reading a model's guess or
 * a computed figure — that distinction is worth a permanent chip on the row.
 *
 * Precedence, for reference: manual > rule > ai > plaid.
 */
import type { ComponentType } from 'react'
import {
  BuildingIcon,
  FunctionSquareIcon,
  PencilIcon,
  SparklesIcon,
  UserIcon,
  WandSparklesIcon,
} from 'lucide-react'
import { formatRatio, type Decimal } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Accepts any of the provenance enums — `CategorySource`, `AttributionSource`,
 * `InflowSource`, `SubscriptionLinkSource` — since they overlap by design.
 */
export type ProvenanceSource =
  | 'manual'
  | 'rule'
  | 'ai'
  | 'plaid'
  | 'plaid_subaccount'
  | 'account_default'
  | 'heuristic'
  | (string & {})

export interface ProvenanceBadgeProps {
  source: ProvenanceSource
  /** 0–1 model confidence. Shown only for AI values, where it means something. */
  confidence?: Decimal
  /** Icon only, for dense tables. The label stays available to screen readers. */
  compact?: boolean
  className?: string
}

/** lucide-react v1 no longer exports a `LucideIcon` type; this is the shape we use. */
type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>

interface Descriptor {
  label: string
  icon: IconComponent
  className: string
  title: string
}

const DESCRIPTORS: Record<string, Descriptor> = {
  manual: {
    label: 'Manual',
    icon: PencilIcon,
    className: 'border-hairline bg-panel-2 text-foreground',
    title: 'You set this by hand. Nothing will overwrite it.',
  },
  rule: {
    label: 'Rule',
    icon: FunctionSquareIcon,
    className: 'border-hairline bg-panel-2 text-dim',
    title: 'Set deterministically by one of your rules.',
  },
  ai: {
    label: 'AI',
    icon: SparklesIcon,
    className: 'border-violet/40 bg-violet/10 text-violet',
    title: 'Suggested by the model. Correct it and it becomes manual.',
  },
  plaid: {
    label: 'Plaid',
    icon: BuildingIcon,
    className: 'border-hairline bg-panel-2 text-dim',
    title: "Straight from the bank's own categorization.",
  },
  plaid_subaccount: {
    label: 'Sub-account',
    icon: UserIcon,
    className: 'border-hairline bg-panel-2 text-dim',
    title: 'Matched on the sub-account owner name or last four digits.',
  },
  account_default: {
    label: 'Account',
    icon: BuildingIcon,
    className: 'border-hairline bg-panel-2 text-dim',
    title: "Inherited from the account's default owner.",
  },
  heuristic: {
    label: 'Detected',
    icon: WandSparklesIcon,
    className: 'border-hairline bg-panel-2 text-dim',
    title: 'Built-in detection — no rule and no model involved.',
  },
}

const UNKNOWN: Descriptor = {
  label: 'Unknown',
  icon: WandSparklesIcon,
  className: 'border-hairline bg-panel-2 text-dim',
  title: 'Source not recorded.',
}

export function ProvenanceBadge({
  source,
  confidence,
  compact = false,
  className,
}: ProvenanceBadgeProps) {
  const descriptor = DESCRIPTORS[source] ?? UNKNOWN
  const Icon = descriptor.icon
  const isAi = source === 'ai'
  const showConfidence = isAi && confidence !== null && confidence !== undefined && !compact

  return (
    <span
      title={descriptor.title}
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1 rounded border px-1.5 py-0.5',
        'font-mono text-[0.625rem] tracking-wide uppercase',
        descriptor.className,
        className,
      )}
    >
      <Icon className="size-3" aria-hidden />
      {compact ? (
        <span className="sr-only">{descriptor.label}</span>
      ) : (
        <>
          {descriptor.label}
          {showConfidence && (
            <span className="tabular opacity-70">{formatRatio(confidence, { decimals: 0 })}</span>
          )}
        </>
      )}
    </span>
  )
}
